import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  user: AuthUser | null;
}

interface ScreenAccess {
  superAdmin: boolean;
  screens: string[];
  loaded: boolean;
  error: string | null;
}

const NO_ACCESS: ScreenAccess = { superAdmin: false, screens: [], loaded: false, error: null };

export interface AuthValue extends AuthState {
  isAccessDenied: boolean;
  isSuperAdmin: boolean;
  /** Spec 050 — screen keys this admin may open. Empty for a super admin, who bypasses checks. */
  screens: string[];
  screensError: string | null;
  hasScreen: (screen: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function extractUser(accessToken: string): AuthUser | null {
  const parsed = parseJwt(accessToken);
  if (!parsed) return null;
  const realmAccess = parsed.realm_access as { roles?: string[] } | undefined;
  return {
    id: (parsed.sub as string) || '',
    email: (parsed.email as string) || '',
    name: (parsed.name as string) || (parsed.preferred_username as string) || '',
    roles: realmAccess?.roles || [],
  };
}

function useAuthState(): AuthValue {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
    user: null,
  });
  const [access, setAccess] = useState<ScreenAccess>(NO_ACCESS);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTokens = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setAuthState({ isAuthenticated: false, isLoading: false, token: null, user: null });
    setAccess(NO_ACCESS);
  }, []);

  const scheduleRefresh = useCallback((expiresIn: number, refreshToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max((expiresIn - 60) * 1000, 10000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!response.ok) throw new Error('Refresh failed');
        const data = await response.json();
        const user = extractUser(data.access_token);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('token_expires_at', String(Date.now() + data.expires_in * 1000));
        setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
        scheduleRefresh(data.expires_in, data.refresh_token);
      } catch {
        clearTokens();
      }
    }, delay);
  }, [clearTokens]);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const expiresAt = localStorage.getItem('token_expires_at');

    if (!accessToken || !refreshToken) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const now = Date.now();
    const expiresAtMs = expiresAt ? parseInt(expiresAt, 10) : 0;

    if (expiresAtMs > now) {
      const user = extractUser(accessToken);
      setAuthState({ isAuthenticated: true, isLoading: false, token: accessToken, user });
      const remainingSeconds = Math.floor((expiresAtMs - now) / 1000);
      scheduleRefresh(remainingSeconds, refreshToken);
    } else {
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          if (!response.ok) throw new Error('Refresh failed');
          const data = await response.json();
          const user = extractUser(data.access_token);
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('token_expires_at', String(Date.now() + data.expires_in * 1000));
          setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
          scheduleRefresh(data.expires_in, data.refresh_token);
        } catch {
          clearTokens();
        }
      })();
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [clearTokens, scheduleRefresh]);

  const isAdmin = authState.user?.roles.includes('ADMIN') ?? false;

  // Spec 050 — resolve screen access once the token is in hand. Non-admins skip
  // it entirely; they are bounced by isAccessDenied before any screen matters.
  useEffect(() => {
    if (!authState.token || !isAdmin) {
      setAccess(NO_ACCESS);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/identity/me/screens`, {
          headers: { Authorization: `Bearer ${authState.token}` },
        });
        if (!response.ok) throw new Error(`Screen access lookup failed (${response.status})`);
        const data = await response.json();
        if (cancelled) return;
        setAccess({
          superAdmin: Boolean(data.superAdmin),
          screens: Array.isArray(data.screens) ? data.screens : [],
          loaded: true,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        // Resolve as loaded-with-error rather than staying pending forever, so
        // the UI can say what went wrong instead of spinning.
        setAccess({
          superAdmin: false,
          screens: [],
          loaded: true,
          error: e instanceof Error ? e.message : 'Could not load your access',
        });
      }
    })();

    return () => { cancelled = true; };
  }, [authState.token, isAdmin]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Invalid email or password');
    }

    const data = await response.json();
    const user = extractUser(data.access_token);
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('token_expires_at', String(Date.now() + data.expires_in * 1000));
    setAuthState({ isAuthenticated: true, isLoading: false, token: data.access_token, user });
    scheduleRefresh(data.expires_in, data.refresh_token);
  }, [scheduleRefresh]);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        // Clear local state anyway
      }
    }
    clearTokens();
  }, [clearTokens]);

  const isAccessDenied = authState.isAuthenticated && authState.user != null && !isAdmin;
  const isSuperAdmin = access.superAdmin || (authState.user?.roles.includes('SUPER_ADMIN') ?? false);

  const hasScreen = useCallback(
    (screen: string) => isSuperAdmin || access.screens.includes(screen),
    [isSuperAdmin, access.screens],
  );

  // Hold the app in its loading state until screens are known. Routing on a
  // half-resolved context is what made the old per-component hook race.
  const isLoading = authState.isLoading || (authState.isAuthenticated && isAdmin && !access.loaded);

  return {
    ...authState,
    isLoading,
    isAccessDenied,
    isSuperAdmin,
    screens: access.screens,
    screensError: access.error,
    hasScreen,
    login,
    logout,
  };
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Spec 050 — single auth instance for the whole portal.
 *
 * This used to be a bare hook, so every component that called it got its own
 * state, its own token-refresh timer and its own loading race — which is why
 * AdminRolesPage carried a comment about <Navigate> firing before the
 * SUPER_ADMIN claim was visible. Adding a screen fetch plus a guard on every
 * route would have multiplied that across ~14 screens.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthState();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
